import { useCallback, useEffect, useState } from 'react'

export type ThemeChoice = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'liveview:theme'
const CHOICES: ThemeChoice[] = ['dark', 'light', 'system']

function readStored(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // Private browsing and blocked site data both throw here; the default is fine.
  }
  return 'dark'
}

function apply(choice: ThemeChoice) {
  const dark =
    choice === 'dark' ||
    (choice === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  document.documentElement.classList.toggle('dark', dark)
}

/**
 * Theme choice, persisted per browser.
 *
 * The initial class is set by an inline script in `index.html` so the page never
 * paints the wrong theme first; this hook takes over from there.
 */
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(readStored)

  useEffect(() => {
    apply(choice)
    try {
      localStorage.setItem(STORAGE_KEY, choice)
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
  }, [choice])

  // Only 'system' cares about the OS flipping underneath us.
  useEffect(() => {
    if (choice !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => apply('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [choice])

  const cycle = useCallback(() => {
    setChoice((prev) => CHOICES[(CHOICES.indexOf(prev) + 1) % CHOICES.length])
  }, [])

  return { theme: choice, setTheme: setChoice, cycleTheme: cycle }
}
